import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { useTripStore } from '@/stores/trip';
import { usePendingCount } from '@/hooks/useApprovals';

export function FloatingActions() {
  const [expanded, setExpanded] = useState(false);
  const role = useAuthStore((s) => s.employee?.role);
  const activeTripId = useTripStore((s) => s.activeTrip?.tripId);

  // Only fetch the badge count for roles that can approve trips
  const canApprove = role === 'manager' || role === 'admin';
  const { data: pendingCount = 0 } = usePendingCount();

  // Only show on mobile
  return (
    <div className="fixed bottom-24 right-4 z-40 lg:hidden">
      {/* Expanded action menu */}
      {expanded && (
        <div className="flex flex-col gap-2 mb-3 animate-[fadeIn_0.2s_ease-out]">
          {activeTripId ? (
            <Link
              to={`/trips/${activeTripId}`}
              onClick={() => setExpanded(false)}
              className="flex items-center gap-2 bg-green-600 text-white rounded-full pl-4 pr-5 py-2.5 shadow-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              View Active Trip
            </Link>
          ) : (
            <Link
              to="/trips/new"
              onClick={() => setExpanded(false)}
              className="flex items-center gap-2 bg-green-600 text-white rounded-full pl-4 pr-5 py-2.5 shadow-lg text-sm font-medium hover:bg-green-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              Start Trip
            </Link>
          )}

          {canApprove && (
            <Link
              to="/approvals"
              onClick={() => setExpanded(false)}
              className="relative flex items-center gap-2 bg-brand-gold-500 text-white rounded-full pl-4 pr-5 py-2.5 shadow-lg text-sm font-medium hover:bg-brand-gold-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Approvals
              {/* Pending count badge */}
              {pendingCount > 0 && (
                <span
                  className="ml-auto bg-white text-brand-gold-600 text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1"
                  aria-label={`${pendingCount} pending`}
                >
                  {pendingCount > 99 ? '99+' : pendingCount}
                </span>
              )}
            </Link>
          )}

          <Link
            to="/trips"
            onClick={() => setExpanded(false)}
            className="flex items-center gap-2 bg-gray-700 text-white rounded-full pl-4 pr-5 py-2.5 shadow-lg text-sm font-medium hover:bg-gray-800 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            My Trips
          </Link>
        </div>
      )}

      {/* FAB button */}
      <button
        onClick={() => setExpanded((p) => !p)}
        aria-label={expanded ? 'Close quick actions' : 'Quick actions'}
        aria-expanded={expanded}
        aria-haspopup="true"
        className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-200
          ${expanded
            ? 'bg-gray-800 rotate-45'
            : 'bg-brand-blue-600 hover:bg-brand-blue-700 hover:scale-105'
          }`}
      >
        <svg className="w-6 h-6 text-white transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>

        {/* Badge on FAB itself when collapsed and there are pending approvals */}
        {canApprove && !expanded && pendingCount > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[1.25rem] h-5 flex items-center justify-center px-1 shadow"
            aria-hidden="true" // already announced via the link badge above
          >
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </button>

      {/*
       * Backdrop — rendered into document.body via a portal so it sits in the
       * root stacking context and reliably covers all page content.
       * z-39 keeps it below the FAB (z-40) but above everything else.
       */}
      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-[39]"
            onClick={() => setExpanded(false)}
            aria-hidden="true"
          />,
          document.body,
        )}
    </div>
  );
}
